// Pure row model for the results rail of the /transcripts/$id canvas: one
// row per CURRENT Mark (the latest machine answer per marker not superseded
// by another machine answer), its verdict, the human reference derived from
// the Labels on the same coordinate, and the evidence window its locator
// covers — plus one row per human-only marker with a saved Label on this
// transcript, its latest Label standing in for the Mark (an unanswered
// human-only marker gets no row). React and network work stay in
// ResultsRail / useLabelInspector.

import type { components } from "../../api/schema";
import { formatJsonValue } from "../../lib/format";
import type { Verdict } from "../../lib/verdictTone";
import type { Assertion } from "../markerAuthoringDraft";
import type { MarkerDisplay } from "../TranscriptAudio";
import { assertionVerdict } from "./labelInspectorDraft";

type Answer = components["schemas"]["AnswerResponse"];
type Utterance = components["schemas"]["TranscriptUtterance"];
type Locator = Answer["locator"];

const VERDICTS: readonly string[] = ["pass", "warn", "fail"];

export interface EvidenceWindow {
  endMs: number;
  startMs: number;
}

export type HumanReference =
  | { kind: "agreed" | "differs"; agrees: boolean; label: string }
  | { kind: "split"; tally: number[]; label: string };

export type ResultsRailValueTone =
  | "error"
  | "fail"
  | "muted"
  | "pass"
  | "plain"
  | "warn";

export interface ResultsRailRow {
  answer: Answer;
  assertion: Assertion | null;
  builtIn: boolean;
  evidence: EvidenceWindow | null;
  // The marker has no Mark on this transcript: `answer` is its latest human
  // Label, so there is nothing to agree with and nothing to re-evaluate.
  humanOnly: boolean;
  humanReference: HumanReference | null;
  // Human Labels on this marker for this transcript, newest first.
  labels: Answer[];
  name: string;
  valueText: string;
  valueTone: ResultsRailValueTone;
  verdict: Verdict | null;
}

export function asVerdict(value: unknown): Verdict | null {
  return typeof value === "string" && VERDICTS.includes(value)
    ? (value as Verdict)
    : null;
}

export function verdictWord(verdict: Verdict): string {
  return verdict === "pass" ? "Pass" : verdict === "warn" ? "Warn" : "Fail";
}

// The one verdict rule (`marker.domain.verdict_of`): a Mark carries a verdict
// only when its marker has an assertion AND the evaluation ran, and then the
// stored value IS the verdict string.
export function markVerdict(
  answer: Answer,
  assertion: Assertion | null,
): Verdict | null {
  if (assertion == null || answer.eval_status !== "ok") {
    return null;
  }
  return asVerdict(answer.value);
}

// A human Label stores the raw typed value; its verdict is the Mark's
// assertion applied to that value (`core.alignment.human_verdict_of`).
export function labelVerdict(
  label: Answer,
  assertion: Assertion | null,
): Verdict | null {
  return assertionVerdict(assertion, label.value);
}

export function latestPerAuthor(
  labels: Answer[],
): Array<Answer & { edited: boolean }> {
  const seen = new Set<string>();
  const latest: Array<Answer & { edited: boolean }> = [];
  for (const label of [...labels].sort(byNewest)) {
    const author = label.author_id ?? `anonymous:${label.id}`;
    if (!seen.has(author)) {
      seen.add(author);
      latest.push({
        ...label,
        edited: labels.some(
          (other) =>
            other.id !== label.id &&
            label.author_id != null &&
            other.author_id === label.author_id,
        ),
      });
    }
  }
  return latest;
}

// Human reference = the majority (strictly more than half) of the latest Label
// per author; verdict-level when the marker has an assertion, exact-value
// otherwise. Law: docs/09a-frontend-screens.md (Transcript results rail).
// Splits are visible here only; core alignment still excludes ties from the
// agreement statistic.
export function humanReference(
  mark: Answer,
  labels: Answer[],
  assertion: Assertion | null,
): HumanReference | null {
  const latest = latestPerAuthor(labels);
  if (latest.length === 0) {
    return null;
  }
  const machineVerdict = markVerdict(mark, assertion);
  const keys = latest.map((label) =>
    assertion != null
      ? (labelVerdict(label, assertion) ?? "")
      : JSON.stringify(label.value ?? null),
  );
  const counts = new Map<string, number>();
  for (const key of keys) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let winner: string | null = null;
  let winnerCount = 0;
  for (const [key, count] of counts) {
    if (count > winnerCount) {
      winner = key;
      winnerCount = count;
    }
  }
  if (winner == null || winner === "") {
    return null;
  }
  if (winnerCount * 2 <= latest.length) {
    const tally = [...counts.values()].sort((left, right) => right - left);
    return { kind: "split", tally, label: `Split ${tally.join("–")}` };
  }
  if (assertion != null) {
    const verdict = asVerdict(winner);
    if (verdict == null) {
      return null;
    }
    const agrees = machineVerdict === verdict;
    return {
      kind: agrees ? "agreed" : "differs",
      agrees,
      label: agrees ? "Agreed" : `Human: ${verdictWord(verdict)}`,
    };
  }
  const agrees = winner === JSON.stringify(mark.value ?? null);
  return {
    kind: agrees ? "agreed" : "differs",
    agrees,
    label: agrees
      ? "Agreed"
      : `Human: ${formatJsonValue(JSON.parse(winner) as unknown)}`,
  };
}

// The conversation index under which a Mark's inline anchor renders: the last
// utterance its locator covers. Audio spans and whole-transcript Marks have no
// utterance anchor.
export function anchorUtteranceIndex(locator: Locator): number | null {
  if (locator?.kind === "utterance_range") {
    return locator.end_index_exclusive - 1;
  }
  if (locator?.kind === "char_span") {
    return locator.utterance_index;
  }
  return null;
}

export function evidenceWindow(
  locator: Locator,
  utterances: Utterance[],
): EvidenceWindow | null {
  if (locator?.kind === "audio_span") {
    return { endMs: locator.end_ms_exclusive, startMs: locator.start_ms };
  }
  let first: Utterance | undefined;
  let last: Utterance | undefined;
  if (locator?.kind === "utterance_range") {
    first = utterances[locator.start_index];
    last = utterances[locator.end_index_exclusive - 1];
  } else if (locator?.kind === "char_span" && locator.utterance_index != null) {
    first = utterances[locator.utterance_index];
    last = first;
  }
  if (first?.start_ms == null || last?.end_ms == null) {
    return null;
  }
  return { endMs: last.end_ms, startMs: first.start_ms };
}

function byNewest(left: Answer, right: Answer): number {
  return (
    Date.parse(right.created_at) - Date.parse(left.created_at) ||
    right.id.localeCompare(left.id)
  );
}

function formatMeasure(value: unknown, unit: string, scale: number): string {
  if (typeof value !== "number") {
    return formatJsonValue(value);
  }
  const scaled = value * scale;
  const text = Number.isInteger(scaled)
    ? String(scaled)
    : scaled.toFixed(unit === "%" ? 0 : 1);
  return unit ? `${text}${unit === "%" ? "%" : ` ${unit}`}` : text;
}

function valueOf(
  answer: Answer,
  verdict: Verdict | null,
): { text: string; tone: ResultsRailValueTone } {
  if (verdict) {
    return { text: verdictWord(verdict), tone: verdict };
  }
  if (answer.eval_status === "not_eligible") {
    return { text: "Not eligible", tone: "muted" };
  }
  if (answer.eval_status === "failed") {
    return { text: "Error", tone: "error" };
  }
  if (answer.eval_status == null && answer.source === "machine") {
    return { text: "…", tone: "muted" };
  }
  if (answer.marker_output === "number" && answer.unit != null) {
    return {
      text: formatMeasure(answer.value, answer.unit, answer.unit_scale),
      tone: "plain",
    };
  }
  return { text: formatJsonValue(answer.value), tone: "plain" };
}

// Judged rows (rule / judge markers) verdict-ordered fail → warn → pass → no
// verdict; Measured rows (built-ins) after them.
function rank(row: ResultsRailRow): number {
  if (row.builtIn) {
    return 4;
  }
  switch (row.verdict) {
    case "fail":
      return 0;
    case "warn":
      return 1;
    case "pass":
      return 2;
    case null:
      return 3;
  }
}

export function resultsRailRows(
  answers: Answer[],
  markerDisplayById: Record<string, MarkerDisplay>,
  utterances: Utterance[],
  pinnedMarkerId?: string,
): ResultsRailRow[] {
  const supersededByMachine = new Set(
    answers.flatMap((answer) =>
      answer.source === "machine" && answer.supersedes_id != null
        ? [answer.supersedes_id]
        : [],
    ),
  );
  const currentByMarker = new Map<string, Answer>();
  for (const answer of answers) {
    if (answer.source !== "machine" || supersededByMachine.has(answer.id)) {
      continue;
    }
    const current = currentByMarker.get(answer.marker_id);
    if (current == null || byNewest(answer, current) < 0) {
      currentByMarker.set(answer.marker_id, answer);
    }
  }
  const labelsByMarker = new Map<string, Answer[]>();
  for (const answer of answers) {
    if (answer.source === "human") {
      labelsByMarker.set(answer.marker_id, [
        ...(labelsByMarker.get(answer.marker_id) ?? []),
        answer,
      ]);
    }
  }
  const markedByMachine = new Set(
    answers.flatMap((answer) =>
      answer.source === "machine" ? [answer.marker_id] : [],
    ),
  );
  const humanOnlyLatest = [...labelsByMarker]
    .filter(([id]) => !markedByMachine.has(id))
    .flatMap(([, labels]) => {
      const latest = [...labels].sort(byNewest)[0];
      return latest ? [latest] : [];
    });
  const rows = [
    ...[...currentByMarker.values()].map((answer) => ({
      answer,
      humanOnly: false,
    })),
    ...humanOnlyLatest.map((answer) => ({ answer, humanOnly: true })),
  ].map(({ answer, humanOnly }): ResultsRailRow => {
    const display = markerDisplayById[answer.marker_id];
    const assertion = display?.assertion ?? null;
    const verdict = humanOnly
      ? labelVerdict(answer, assertion)
      : markVerdict(answer, assertion);
    const value = valueOf(answer, verdict);
    const labels = [...(labelsByMarker.get(answer.marker_id) ?? [])].sort(
      byNewest,
    );
    return {
      answer,
      assertion,
      builtIn: display?.builtinKey != null,
      evidence: evidenceWindow(answer.locator, utterances),
      humanOnly,
      humanReference: humanOnly
        ? null
        : humanReference(answer, labels, assertion),
      labels,
      name:
        answer.rendered_text?.trim() ||
        display?.name ||
        answer.marker_display_name,
      valueText: value.text,
      valueTone: value.tone,
      verdict,
    };
  });
  return rows.sort(
    (left, right) =>
      Number(right.answer.marker_id === pinnedMarkerId) -
        Number(left.answer.marker_id === pinnedMarkerId) ||
      rank(left) - rank(right) ||
      left.name.localeCompare(right.name),
  );
}

export function resultsRailSummary(rows: ResultsRailRow[]): {
  failed: number;
  needsReview: number;
  passed: number;
} {
  const judged = rows.filter((row) => !row.builtIn);
  return {
    failed: judged.filter((row) => row.verdict === "fail").length,
    needsReview: judged.filter((row) => row.answer.needs_review).length,
    passed: judged.filter((row) => row.verdict === "pass").length,
  };
}

export function resultsRailGlyph(row: ResultsRailRow): {
  glyph: string;
  tone: Verdict | "skipped" | "none";
} {
  if (row.verdict != null) {
    return {
      glyph: row.verdict === "fail" ? "✗" : row.verdict === "warn" ? "!" : "✓",
      tone: row.verdict,
    };
  }
  if (["failed", "not_eligible"].includes(row.answer.eval_status ?? "")) {
    return { glyph: "–", tone: "skipped" };
  }
  return { glyph: "·", tone: "none" };
}

// The row `?label=<answerId>` expands: the Mark itself, or — when the id names
// a human Label on the same coordinate — that Label's marker row.
export function expandedRow(
  rows: ResultsRailRow[],
  label: string | undefined,
  answers: Answer[],
): ResultsRailRow | undefined {
  if (label === undefined || label === "new") {
    return undefined;
  }
  const direct = rows.find((row) => row.answer.id === label);
  if (direct) {
    return direct;
  }
  const labeled = answers.find((answer) => answer.id === label);
  return labeled
    ? rows.find((row) => row.answer.marker_id === labeled.marker_id)
    : undefined;
}

// Arriving from Review with `?feed=` but no `?label=`: the first row routed to
// review opens.
export function firstRoutedRow(
  rows: ResultsRailRow[],
): ResultsRailRow | undefined {
  return (
    rows.find((row) => row.answer.needs_review && !row.builtIn) ??
    rows.find((row) => row.answer.review_routed && !row.builtIn)
  );
}

export function nextRow(
  rows: ResultsRailRow[],
  currentId: string | undefined,
): ResultsRailRow | undefined {
  const index = rows.findIndex((row) => row.answer.id === currentId);
  return rows.slice(index + 1).find((row) => !row.builtIn);
}

// The re-evaluate checklist: every marker with a current Mark on this
// transcript (with its value), then the agent's remaining markers not yet
// evaluated here -- excluding human-only ones. A human-only marker has no
// evaluator to re-run: its per-row Re-evaluate is already hidden, and since
// the firing set excludes human-only current revisions, enqueueing one
// resolves to zero members and the reader watches a run that can never
// produce a Mark.
export interface RerunMarkerOption {
  id: string;
  name: string;
  valueText: string;
}

export function rerunMarkerOptions(
  rows: ResultsRailRow[],
  markers: Array<{
    builtin_key: string | null;
    current_revision: { display_name: string; evaluator: string | null };
    id: string;
  }>,
): RerunMarkerOption[] {
  const marked = rows.filter((row) => !row.humanOnly);
  const seen = new Set(marked.map((row) => row.answer.marker_id));
  return [
    ...marked.map((row) => ({
      id: row.answer.marker_id,
      name: row.name,
      valueText: row.valueText,
    })),
    ...markers
      .filter(
        (marker) =>
          !seen.has(marker.id) && marker.current_revision.evaluator !== null,
      )
      .map((marker) => ({
        id: marker.id,
        name: marker.current_revision.display_name,
        valueText: "—",
      })),
  ];
}
